import { useMemo } from 'react';

import {
  BANK_DOSSIER_MOCK_ENABLED,
  Constants,
} from '../../../constants/bankDossier';
import { StatusCode } from '../../enumerations';
import { myHeadersWithTokenContentType } from '../../global';
import {
  INVALID_SESSION_MESSAGE,
  isAbortError,
  UNAUTHENTICATED_MESSAGE,
} from '../../http';
import { GetWorkOrder } from '../index';
import { GetWorkOrderProps, WorkOrderData } from '../types';
import { toBankDossier } from './adapter';
import { bankDossierApiUrl, isBankDossierApiEnabled } from './config';
import { getBankDossier as getMockBankDossier } from './mock';
import {
  BankDossier,
  BankDossierEnvelope,
  BankDossierRequest,
  BankDossierResponse,
} from './types';

/** The avaliahub-api caps `work_order_ids` at 100 per call. */
const BATCH_SIZE = 100;

/**
 * Failure of an avaliahub-api call. Its message is always safe to show: it
 * never carries the control center session messages, so `getErrorMessage`
 * does not end the session because of this service.
 */
export class BankDossierError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'BankDossierError';
    this.status = status;
  }
}

interface RawResult<T> {
  status: number;
  envelope: BankDossierEnvelope<T> | null;
}

const errorMessage = <T>({ status, envelope }: RawResult<T>): string => {
  // A 401 here is about the avaliahub-api session only: the control center
  // session stays (unlike `requestJson`, which drops it on any 401).
  if (status === 401) return Constants.api.unauthenticated;
  const description = envelope?.detail?.description;
  if (
    !description ||
    description === INVALID_SESSION_MESSAGE ||
    description === UNAUTHENTICATED_MESSAGE
  ) {
    return Constants.api.unavailable;
  }
  return description;
};

const send = async <T>(
  path: string,
  init: RequestInit,
  signal?: AbortSignal
): Promise<RawResult<T>> => {
  if (!isBankDossierApiEnabled) {
    throw new BankDossierError(0, Constants.api.notConfigured);
  }
  let response: Response;
  try {
    response = await fetch(`${bankDossierApiUrl}/api/V1/work-orders${path}`, {
      ...init,
      headers: myHeadersWithTokenContentType(),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new BankDossierError(0, Constants.api.unavailable);
  }
  const text = await response.text();
  try {
    const envelope = text ? (JSON.parse(text) as BankDossierEnvelope<T>) : null;
    return { status: response.status, envelope };
  } catch {
    return { status: response.status, envelope: null };
  }
};

const ensureOk = <T>(result: RawResult<T>): BankDossierEnvelope<T> => {
  const { status, envelope } = result;
  const succeeded =
    status >= 200 &&
    status < 300 &&
    !envelope?.detail?.description &&
    (envelope?.detail?.status_code ?? StatusCode.OK) === StatusCode.OK;
  if (!succeeded) throw new BankDossierError(status, errorMessage(result));
  return envelope ?? { detail: { status: 'ok', status_code: StatusCode.OK } };
};

const request = async <T>(
  path: string,
  init: RequestInit,
  signal?: AbortSignal
): Promise<BankDossierEnvelope<T>> =>
  ensureOk(await send<T>(path, init, signal));

/** `null` when the work order has no dossier (404). */
export const getBankDossier = async (
  id: number,
  signal?: AbortSignal
): Promise<BankDossierResponse | null> => {
  const result = await send<BankDossierResponse>(
    `/${id}/bank-dossier`,
    { method: 'GET' },
    signal
  );
  if (result.status === 404) return null;
  const { data } = ensureOk(result);
  if (!data) throw new BankDossierError(result.status, Constants.api.invalid);
  return data;
};

/** Only the work orders that have a dossier come back, in batches of 100 ids. */
export const getBankDossiers = async (
  ids: number[],
  signal?: AbortSignal
): Promise<BankDossierResponse[]> => {
  const unique = Array.from(new Set(ids));
  const batches: number[][] = [];
  for (let start = 0; start < unique.length; start += BATCH_SIZE) {
    batches.push(unique.slice(start, start + BATCH_SIZE));
  }
  const responses = await Promise.all(
    batches.map((batch) =>
      request<BankDossierResponse[]>(
        `/bank-dossiers?work_order_ids=${batch.join(',')}`,
        { method: 'GET' },
        signal
      )
    )
  );
  return responses.flatMap((response) => response.data ?? []);
};

export const createBankDossier = async (
  id: number,
  body: BankDossierRequest
): Promise<void> => {
  await request<{ work_order_id: number }>(`/${id}/bank-dossier`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/** Creates the dossier when it does not exist yet, so a retry never hits 409. */
export const updateBankDossier = async (
  id: number,
  body: BankDossierRequest
): Promise<void> => {
  await request<null>(`/${id}/bank-dossier`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/** Sets `archived_at`; the archived dossier stays readable. */
export const archiveBankDossier = async (id: number): Promise<void> => {
  await request<null>(`/${id}/bank-dossier`, { method: 'DELETE' });
};

export interface GetWorkOrderWithDossierProps extends GetWorkOrderProps {
  /** Set when the dossier could not be loaded (not when the OS has none). */
  dossierError?: string;
}

/**
 * `GetWorkOrder` plus its bank dossier, fetched in parallel. The work order
 * never depends on the avaliahub-api: if the dossier fails, `bank_dossier`
 * is `null` and `dossierError` says why.
 */
export const getWorkOrderWithDossier = async (
  id: number
): Promise<GetWorkOrderWithDossierProps> => {
  if (!isBankDossierApiEnabled) return GetWorkOrder(id);

  const [workOrder, dossier] = await Promise.allSettled([
    GetWorkOrder(id),
    getBankDossier(id),
  ]);
  if (workOrder.status === 'rejected') throw workOrder.reason;

  const response = workOrder.value;
  if (!response.data) return response;

  if (dossier.status === 'rejected') {
    return {
      ...response,
      data: { ...response.data, bank_dossier: null },
      dossierError:
        dossier.reason instanceof BankDossierError
          ? dossier.reason.message
          : Constants.api.unavailable,
    };
  }
  return {
    ...response,
    data: {
      ...response.data,
      bank_dossier: dossier.value ? toBankDossier(dossier.value) : null,
    },
  };
};

/**
 * Resolves the bank dossier of a work order: the API data when present. The
 * deterministic mock only fills in while the avaliahub-api is not configured.
 */
export const resolveBankDossier = (
  workOrder: WorkOrderData | null | undefined
): BankDossier | null => {
  if (!workOrder) return null;
  if (workOrder.bank_dossier) return workOrder.bank_dossier;
  if (isBankDossierApiEnabled || !BANK_DOSSIER_MOCK_ENABLED) return null;
  return getMockBankDossier(workOrder);
};

export const useBankDossier = (
  workOrder: WorkOrderData | null | undefined
): BankDossier | null =>
  useMemo(() => resolveBankDossier(workOrder), [workOrder]);

/** Files that belong to the work order itself (not to sibling requests of the folder). */
export const ownFiles = (
  dossier: BankDossier,
  workOrderId: number
): BankDossier['files'] =>
  dossier.files.filter((file) => file.work_order_id === workOrderId);

export const latestReport = (
  dossier: BankDossier,
  workOrderId: number
): BankDossier['files'][number] | null =>
  ownFiles(dossier, workOrderId).find((file) => file.is_latest_report) ?? null;

export { emptyBankDossier, toBankDossier } from './adapter';
export { isBankDossierApiEnabled } from './config';
export * from './lookup';
export * from './types';
